import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import config from '../config.js'
import EditorView from '../views/EditorView.vue'

const routes = [
  {
    path: '/',
    name: 'Editor',
    component: EditorView,
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: EditorView, // Use EditorView as the base component
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: EditorView, // Use EditorView as the base component
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/invite/:code',
    name: 'AcceptInvitation',
    component: () => import('../views/AcceptInvitationView.vue'),
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPasswordView.vue'),
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/job-recommendations',
    name: 'JobRecommendations',
    component: () => import('../views/JobRecommendationsView.vue'),
    meta: {
      requiresAuth: false // Public route
    }
  },
  {
    path: '/interview-debrief',
    name: 'InterviewDebrief',
    component: () => import('../views/JobRecommendationsView.vue'),
    meta: {
      requiresAuth: false // Public route; same chat view, interview-debrief mode
    }
  },
  {
    path: '/resume-library',
    name: 'ResumeLibrary',
    component: () => import('../views/ResumeLibraryView.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/SettingsView.vue'),
    meta: {
      requiresAuth: true
    }
  },
  // Add more routes here as needed
  // Example protected route:
  // {
  //   path: '/dashboard',
  //   name: 'Dashboard',
  //   component: () => import('../views/DashboardView.vue'),
  //   meta: {
  //     requiresAuth: true
  //   }
  // }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

/**
 * Route guard for authentication
 * Checks if user is authenticated before accessing protected routes
 */
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if the route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to home page and show auth modal
    next('/')
    
    // Show login modal after a short delay to allow route transition
    setTimeout(() => {
      // You might want to trigger the auth modal here
      // For now, we'll just log a message
      console.log('Authentication required for route:', to.path)
    }, 100)
  } else if (requiresAuth && authStore.isAuthenticated) {
    // Check if email verification is required for this route
    const requiresVerifiedEmail = to.matched.some(record => record.meta.requiresVerifiedEmail)
    
    if (requiresVerifiedEmail) {
      // Check if new verification flow is enabled
      const useNewVerificationFlow = config.features?.emailVerificationFix || false
      
      if (useNewVerificationFlow && authStore.needsVerification) {
        // Redirect to verification page or show verification modal
        console.log('Email verification required for route:', to.path)
        
        // For now, we'll allow access but log a warning
        // In a real implementation, you might redirect to a verification page
        // or show a verification modal
        next()
      } else {
        next()
      }
    } else {
      next()
    }
  } else {
    // Continue to the route
    next()
  }
})

/**
 * Optional: Route guard for authenticated users trying to access auth pages
 * Example: Prevent logged-in users from accessing login/register pages
 */
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if the route is an auth page (login/register)
  const isAuthPage = to.path === '/login' || to.path === '/register'
  
  if (isAuthPage && authStore.isAuthenticated) {
    // Redirect authenticated users away from auth pages
    next('/')
  } else {
    next()
  }
})

export default router
